import { safeWindow } from './storage';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown> | undefined;
  userId?: string | undefined;
  sessionId?: string | undefined;
  requestId?: string | undefined;
}

class Logger {
  private buffer: LogEntry[] = [];
  private readonly flushInterval = 5000;
  private readonly maxBufferSize = 100;
  // Remote log sink. There is no log-ingest endpoint on the biotk-api, so the
  // old hardcoded '/api/logs' 405'd on every flush in production. Remote
  // logging is now opt-in: set VITE_LOG_ENDPOINT to a real ingest URL to
  // enable it. When unset, logs stay console-only (dev) and are dropped on
  // flush (prod) — no failed network calls.
  private readonly endpoint: string | undefined = import.meta.env.VITE_LOG_ENDPOINT;
  private sessionId: string;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private logLevel: LogLevel = 'info';

  constructor() {
    this.sessionId = this.generateSessionId();
    this.initLogLevel();
    this.startFlushTimer();

    // Flush on page unload
    safeWindow.addEventListener('beforeunload', () => {
      void this.flush();
    });
  }

  private initLogLevel() {
    const envLevel = import.meta.env.VITE_LOG_LEVEL as LogLevel | undefined;
    if (envLevel && ['debug', 'info', 'warn', 'error'].includes(envLevel)) {
      this.logLevel = envLevel;
    } else if (import.meta.env.DEV) {
      this.logLevel = 'debug';
    }
  }

  private generateSessionId(): string {
    return `sess-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    return levels.indexOf(level) >= levels.indexOf(this.logLevel);
  }

  private startFlushTimer() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flushTimer = setInterval(() => {
      void this.flush();
    }, this.flushInterval);
  }

  debug(message: string, context?: Record<string, unknown>) {
    this.log('debug', message, context);
  }

  info(message: string, context?: Record<string, unknown>) {
    this.log('info', message, context);
  }

  warn(message: string, context?: Record<string, unknown>) {
    this.log('warn', message, context);
  }

  error(message: string, context?: Record<string, unknown>) {
    this.log('error', message, context);
  }

  private log(level: LogLevel, message: string, context?: Record<string, unknown>) {
    if (!this.shouldLog(level)) return;

    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      context,
      sessionId: this.sessionId,
    };

    // Always log to console in development
    if (import.meta.env.DEV) {
      const consoleMethod = level === 'debug' ? 'log' : level;
      // eslint-disable-next-line no-console
      console[consoleMethod](`[${level.toUpperCase()}] ${message}`, context ?? '');
    }

    // Buffer for batch sending to server
    this.buffer.push(entry);

    // Immediate flush for errors or if buffer is full
    if (level === 'error' || this.buffer.length >= this.maxBufferSize) {
      void this.flush();
    }
  }

  async flush() {
    if (this.buffer.length === 0) return;

    const logs = [...this.buffer];
    this.buffer = [];

    // Only send to server in production AND only when a remote ingest endpoint
    // is configured (VITE_LOG_ENDPOINT). Without it, drop the batch — no 405s.
    if (import.meta.env.PROD && this.endpoint) {
      try {
        await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ logs }),
        });
      } catch {
        // Re-queue on failure (limited to prevent memory issues)
        this.buffer = [...logs.slice(-50), ...this.buffer].slice(0, this.maxBufferSize);
      }
    }
  }

  setUserId(userId: string) {
    // Store for future log entries (would need to add to entries)
    this.info('User identified', { userId });
  }

  getSessionId(): string {
    return this.sessionId;
  }
}

export const logger = new Logger();
